<template>
  <div class="eCartItem__top">
    <ImageField
      :key="productImage"
      class="eCartItem__image"
      :obj="{ url: productImage, alt: 'Product image', width: 228, height: 285 }"
      width-param="width"
      sizes="300px"
    />
    <div class="eCartItem__info">
      <div class="eCartItem__title" :aria-label="`Product title ${product.title}`">{{ product.title }}</div>
      <div v-if="product.id" class="eCartItem__review-wrapper">
        <div class="yotpo-widget-instance" data-yotpo-instance-id="498880" :data-yotpo-product-id="product.id"></div>
      </div>
      <ProductPrice :product="product" :selectedFirstOption="selectedFirstLevelOption" />
    </div>
  </div>
  <!-- Product Options -->
  <ProductOptions
    :product="product"
    :selectedOptions="productForm"
    :isSecondTurn="isSecondTurn"
    :showSecondTurnSelectors="true"
    @on-option-change="onOptionChange"
  />

  <!-- Product ATC -->
  <div v-if="productForm.selectedVariant">
    <button
      v-if="productForm.selectedVariant.available"
      data-add-to-cart
      class="btn btn--secondary btn--medium pm__atc"
      :aria-label="`${isCartUpsell ? 'Add' : 'Update'} product ${product.title} in cart`"
      @click="updateCartItem"
    >
      {{ isCartUpsell ? 'ADD TO CART' : 'UPDATE' }}
    </button>
    <div v-else>
      <button
        v-if="!selectedFirstLevelOption.bis"
        data-add-to-cart
        disabled
        class="btn btn--secondary btn--medium pm__atc"
        :aria-label="`Product ${product.title} is out of stock`"
      >
        OUT OF STOCK
      </button>
      <button v-else class="btn btn--secondary btn--medium pm__atc" @click="bisDrawer">Notify Me When Available</button>
    </div>
  </div>
  <div v-else>
    <button
      data-add-to-cart
      @mouseover="mouseOverUnselectBtn = true"
      @mouseleave="mouseOverUnselectBtn = false"
      class="btn btn--secondary btn--medium pm__atc"
    >
      <span
        v-if="!mouseOverUnselectBtn"
        :aria-label="`${isCartUpsell ? 'Add' : 'Update'} product ${product.title} in cart`"
        >{{ isCartUpsell ? 'ADD TO CART' : 'UPDATE' }}</span
      >
      <span v-else>SELECT {{ unselectBtnName }}</span>
    </button>
  </div>
</template>

<script setup>
  import { ref, toRefs, onBeforeMount, reactive, computed } from 'vue';
  import { useStore } from 'vuex';
  import ImageField from '../../global/image.vue';
  import ProductPrice from '../../pdp-first-fold/components/product-price.vue';
  import ProductOptions from '../../pdp-first-fold/components/product-options/index.vue';
  import { getSecondTurnVariantForOption } from '../../../utils/second-turn';

  const props = defineProps({
    product: {
      type: Object,
      default: '',
    },

    variantId: {
      type: String,
      default: '',
    },

    line: {
      type: String,
      default: '',
    },

    lineKey: {
      type: String,
      default: '',
    },

    quantity: {
      type: String,
      default: '',
    },

    isCartUpsell: {
      type: Boolean,
      default: false,
    },
  });

  const { product, variantId, line, lineKey, quantity, isCartUpsell } = toRefs(props);

  const store = useStore();

  var productForm = reactive({
    selectedVariant: null,
  });
  var mouseOverUnselectBtn = ref(false);

  onBeforeMount(() => {
    initialSetup();
    window.refreshYotpoData();
  });

  const selectedFirstLevelOption = computed(() => {
    return store.state.pdp.firstLevelOptions?.[product.value.id];
  });

  const productImage = computed(() => {
    if (productForm.selectedVariant) {
      return productForm.selectedVariant.featured_image;
    } else {
      const { title, images } = product.value;
      const { Color, Town } = productForm;
      var imageData = window.getProductImages(images, Color, title, Town);

      return imageData[0].src;
    }
  });

  const isSecondTurn = computed(() => {
    return product.value.tags.includes('Second Turn');
  });

  const unselectBtnName = computed(() => {
    const { options } = product.value;
    let name;
    for (let option in options) {
      if (!productForm[options[option].name]) {
        name = options[option].name;
        break;
      }
    }
    return name;
  });

  const initialSetup = () => {
    const { options, variants } = product.value;

    // Find the selectedVariant or use the first variant
    const selectedVariant = variantId.value ? variants.find((variant) => variant.id == variantId.value) : variants[0];

    // Set the selectedVariant
    productForm.selectedVariant = selectedVariant;

    // Set options based on the selectedVariant
    options.forEach((option, index) => {
      productForm[option.name] = selectedVariant[`option${index + 1}`];
    });
  };

  const onOptionChange = (key, value, index, length) => {
    const secondTurnVariant =
      isSecondTurn.value && getSecondTurnVariantForOption(product.value, productForm, key, value);

    if (secondTurnVariant) {
      product.value.options.forEach((option, optionIndex) => {
        productForm[option.name] = secondTurnVariant[`option${optionIndex + 1}`];
      });
    } else {
      productForm[key] = value;
    }

    updateVariantValue();

    // if(key != 'Color' && key != 'Title'){
    //   updateColorOOS(value,index,length)
    // }
  };

  const updateVariantValue = () => {
    let variantValue = '';
    const { options } = product.value;
    options.forEach((option, index, array) => {
      variantValue += productForm[option.name];
      if (index != array.length - 1) variantValue += ' / ';
    });

    updateSelectedVariant(variantValue);
  };

  const updateSelectedVariant = (value) => {
    const variant = product.value.variants.find((item) => item.value === value);
    if (variant) productForm.selectedVariant = variant;
    else productForm.selectedVariant = null;
  };

  const bisDrawer = () => {
    window.toggleDrawer(true, 'BISModal', {
      productId: product.value.id,
      variantId: productForm.selectedVariant.id,
      bisDate: selectedFirstLevelOption.value.bisDate,
    });
  };

  const updateCartItem = async () => {
    var key = lineKey.value;
    var variantId = productForm.selectedVariant.id;
    var variantQty = quantity.value;

    if (isCartUpsell.value) {
      await window.Additem({
        id: variantId,
        quantity: 1,
        properties: {
          ...(product.value.final_sale_msg && { Note: product.value.final_sale_msg }),
        },
      });
    } else {      
      let itemsToChangeOrAdd = {
        line: line.value,
        change: [
          {
            id: key,
            quantity: 0,
          },
        ], 
        add: [
          {
            id: variantId,
            quantity: variantQty,
            properties: {
              ...(product.value.final_sale_msg && { Note: product.value.final_sale_msg }),
            },
          },
        ] 
      };
      await window.changeThenAdd(itemsToChangeOrAdd);
    }

    window.toggleDrawer(false, 'EditCartItem');
  };
</script>
